/**
 * Statsig A/B Testing Utilities
 *
 * This file contains helper functions for Statsig experiment tracking and user identification.
 */

import { Statsig } from 'statsig-react';
import brandConfig from '../config/brand';

// Statsig client key from environment variables
export const STATSIG_CLIENT_KEY = process.env.REACT_APP_STATSIG_CLIENT_KEY || 'client-fVnTcBD5dnY1XprACBZPkC2CW4os8zILDIq3fXZzoLC';

// Experiment names
export const EXPERIMENTS = {
  SELF_SERVE_FORM: 'self_serve_form_test_v6',
  QUOTE_PAGE_V1: 'quote_page_v1',
  // Quote-page primary CTA copy A/B. Three planned variants (monthly /
  // one-time copies):
  //   control:      "Start My Service"        / "Confirm Services"
  //   reserve_slot: "Reserve My Weekly Slot"  / "Schedule My Service"
  //   approve:      "Approve Quote"           / "Approve Quote"
  // Operator wires the variants in the Statsig dashboard. Until the
  // experiment exists, the code falls back to control copy.
  QUOTE_CTA_COPY: 'quote_cta_copy_v1',
  // Step-1 submit button copy. Control is the current "Next". Treatment
  // is "Get my quote" (first-person possessive). Fallback is control so
  // an unwired experiment does not change live copy.
  STEP1_CTA_COPY: 'step1_cta_copy_v1',
};

export const STEP1_CTA_CONTROL = 'Next';
export const STEP1_CTA_TREATMENT = 'Get my quote';

// Feature gates (on/off, not multi-variant experiments)
export const GATES = {
  // Optional Ron intake questions, 50/50; guardrail: split_direct_request_completed.
  DIRECT_PROPERTY_DETAILS: 'direct_property_details',
  CONTACT_ID_FALLBACK: 'contact_id_fallback',
  // Existing-customer price anchoring. When on, the quote screen sends the
  // service address, name and phone to the pricing engine, which quotes an
  // address we already service on its own billed rate instead of the regional
  // standard, or routes it to a manual quote when it cannot price it
  // defensibly. When off, those fields are withheld and the engine behaves
  // exactly as it did before the feature existed.
  EXISTING_CUSTOMER_ANCHORING: 'existing_customer_anchoring',
  // Poll-for-signed on a submit timeout. When on, a submit that hits the
  // client-side axios timeout stops throwing an error immediately and
  // instead polls the quote read endpoint for the terminal signed state;
  // if it lands, the customer sees the success screen. The submit handler
  // writes status='signed' before it responds and the heavy customer-
  // create + card-store + $0-auth path can run a few seconds past the
  // client ceiling on a cold start or a slow QBO day, so the timeout was
  // firing a "timeout of 15000ms exceeded" error on signings that had in
  // fact succeeded. When off, behavior is exactly as before (throw on
  // timeout). Guardrail metric: form completion (quote_signed).
  SUBMIT_POLL_ON_TIMEOUT: 'submit_poll_on_timeout',
  // Fast provisioning on the signing submit. When on, the client stamps
  // fastProvisioning=true into the submit body; the server then defers the
  // card default/verified PATCH to the background post-sign step and does
  // not block the customer on the post-sign trigger, so the customer waits
  // only on the card store and the $0 decline check. When off, behavior is
  // unchanged (PATCH synchronous, 2s post-sign nudge). Guardrail metric:
  // form completion (quote_signed).
  SUBMIT_FAST_PROVISIONING: 'submit_fast_provisioning',
  // Targeted QBO customer lookup on the signing submit. When on, the client
  // stamps targetedLookup=true into the submit body and the server resolves
  // a returning customer with a targeted email query plus a live
  // phone-ownership confirmation, instead of reading the whole customer
  // book on every signing. Any uncertain shape (email miss, shared email,
  // unconfirmed or unknown phone owner, stale index) still runs the legacy
  // scan, so the treatment changes latency, not matching semantics. When
  // off, behavior is exactly as before. Guardrail metric: form completion
  // (quote_signed).
  SUBMIT_TARGETED_LOOKUP: 'submit_targeted_lookup',
  // Revision/template echo validation on the customer quote signing path.
  // It is rolled out 50/50 with quote completion as the guardrail. The
  // server remains backward compatible for healthy links rendered before
  // this metadata existed.
  QUOTE_REVISION_SIGNING: 'quote_revision_signing',
  QUOTE_SUBMISSION_CONFIRMATION: 'quote_submission_confirmation',
  SERVICE_OPTION_EXISTING_QUOTE: 'service_option_existing_quote',
};

export const isQuoteRepairEnabled = gate => {
  try {
    const forced = sessionStorage.getItem(`force_${gate}`);
    if (forced === 'on' || forced === 'off') return forced === 'on';
    return Statsig.checkGate(gate) === true;
  } catch { return false; }
};

// Hold one decision across step initialization, manual submit and unload.
// Roll out 50/50 with form completion as the guardrail before retiring it.
let contactIdFallbackDecision;
export const isContactIdFallbackEnabled = () => {
  if (contactIdFallbackDecision !== undefined) return contactIdFallbackDecision;
  try {
    const forced = sessionStorage.getItem('force_contact_id_fallback');
    if (forced === 'on' || forced === 'off') {
      contactIdFallbackDecision = forced === 'on';
      return contactIdFallbackDecision;
    }
  } catch { /* use the server-assigned gate */ }
  try {
    contactIdFallbackDecision = Statsig.checkGate(GATES.CONTACT_ID_FALLBACK) === true;
  } catch {
    contactIdFallbackDecision = false;
  }
  return contactIdFallbackDecision;
};

// Decided once per session and cached. The displayed price and the price the
// backend recomputes at submit must come from the same decision, and the two
// happen minutes apart across two components; re-reading the gate each time
// would let a mid-session config change show one number and bill another.
let _ecAnchoringDecision = null;

/**
 * Whether existing-customer price anchoring is on for this visitor.
 *
 * Defaults to false on any failure (Statsig not ready, network, storage
 * blocked). False is the pre-feature behavior, so a failure degrades to the
 * standard price rather than to a wrong one.
 *
 * QA override: sessionStorage.force_ec_anchoring = 'on' | 'off'.
 */
export const isExistingCustomerAnchoringEnabled = () => {
  if (_ecAnchoringDecision !== null) return _ecAnchoringDecision;

  try {
    const forced = sessionStorage.getItem('force_ec_anchoring');
    if (forced === 'on' || forced === 'off') {
      _ecAnchoringDecision = forced === 'on';
      return _ecAnchoringDecision;
    }
  } catch { /* storage blocked; fall through to the gate */ }

  try {
    _ecAnchoringDecision = Statsig.checkGate(GATES.EXISTING_CUSTOMER_ANCHORING) === true;
  } catch (error) {
    _ecAnchoringDecision = false;
  }
  return _ecAnchoringDecision;
};

/**
 * Whether poll-for-signed-on-timeout is on for this visitor.
 *
 * Read fresh at submit time (unlike the anchoring decision, this only
 * affects the timeout recovery path and does not need to agree with any
 * earlier-rendered value, so there is nothing to cache for consistency).
 *
 * Defaults to false on any failure (Statsig not ready, network, storage
 * blocked). False is the pre-feature behavior (throw on timeout), so a
 * failure degrades to the existing behavior rather than a new one.
 *
 * QA override: sessionStorage.force_submit_poll = 'on' | 'off'.
 */
export const isSubmitPollOnTimeoutEnabled = () => {
  try {
    const forced = sessionStorage.getItem('force_submit_poll');
    if (forced === 'on' || forced === 'off') return forced === 'on';
  } catch { /* storage blocked; fall through to the gate */ }

  try {
    return Statsig.checkGate(GATES.SUBMIT_POLL_ON_TIMEOUT) === true;
  } catch (error) {
    return false;
  }
};

/**
 * Whether fast provisioning is on for this visitor. Stamped into the submit
 * body so the server defers the card-flag PATCH and does not block on the
 * post-sign trigger.
 *
 * Defaults to false on any failure (Statsig not ready, network, storage
 * blocked). False is the pre-feature behavior, so a failure degrades to the
 * current synchronous path rather than a new one.
 *
 * QA override: sessionStorage.force_submit_fast_provisioning = 'on' | 'off'.
 */
export const isSubmitFastProvisioningEnabled = () => {
  try {
    const forced = sessionStorage.getItem('force_submit_fast_provisioning');
    if (forced === 'on' || forced === 'off') return forced === 'on';
  } catch { /* storage blocked; fall through to the gate */ }

  try {
    return Statsig.checkGate(GATES.SUBMIT_FAST_PROVISIONING) === true;
  } catch (error) {
    return false;
  }
};

export const isSubmitTargetedLookupEnabled = () => {
  try {
    const forced = sessionStorage.getItem('force_submit_targeted_lookup');
    if (forced === 'on' || forced === 'off') return forced === 'on';
  } catch { /* storage blocked; fall through to the gate */ }

  try {
    return Statsig.checkGate(GATES.SUBMIT_TARGETED_LOOKUP) === true;
  } catch (error) {
    return false;
  }
};

export const isQuoteRevisionSigningEnabled = () => {
  try {
    const forced = sessionStorage.getItem('force_quote_revision_signing');
    if (forced === 'on' || forced === 'off') return forced === 'on';
  } catch { /* storage blocked; fall through to the gate */ }
  try {
    return Statsig.checkGate(GATES.QUOTE_REVISION_SIGNING) === true;
  } catch {
    return false;
  }
};

// Event names for tracking
export const EVENTS = {
  // Funnel step events
  STEP_1_COMPLETED: 'step_1_completed',
  STEP_2_COMPLETED: 'step_2_completed',
  STEP_3_COMPLETED: 'step_3_completed',
  POOL_SIZE_DETECTED: 'pool_size_detected',
  FORM_COMPLETED: 'form_completed',

  // Self-serve specific events
  SELF_SERVE_OPTED_IN: 'self_serve_opted_in',
  QUOTE_SHOWN: 'quote_shown',
  QUOTE_CLAIMED: 'quote_claimed',

  // Quote page engagement events
  EMAIL_ENTERED: 'email_entered',
  DETAILS_TOGGLED: 'details_toggled',
  FAQ_OPENED: 'faq_opened',

  // Quote page events
  QUOTE_PAGE_VIEWED: 'quote_page_viewed',
  QUOTE_SIGNED: 'quote_signed',
  PAYMENT_METHOD_SELECTED: 'payment_method_selected',

  // Quote page funnel events
  QUOTE_SECTION_VIEWED: 'quote_section_viewed',
  QUOTE_SCROLL_DEPTH: 'quote_scroll_depth',
  QUOTE_SUBMIT_ATTEMPTED: 'quote_submit_attempted',
  QUOTE_SUBMIT_ERROR: 'quote_submit_error',
  PAYMENT_FIELD_FOCUSED: 'payment_field_focused',
  QUOTE_HOWHEARD_SUBMITTED: 'quote_howheard_submitted',

  // Mobile abandon instrumentation: granular drop-off + interaction
  // signals on the quote page so we can spot the worst-offending step
  // and target the next CRO experiment at it instead of guessing.
  QUOTE_PAYMENT_FIELD_BLURRED_EMPTY: 'quote_payment_field_blurred_empty',
  QUOTE_ACH_REVEALED: 'quote_ach_revealed',
  QUOTE_TOKENIZE_STARTED: 'quote_tokenize_started',
  QUOTE_TOKENIZE_COMPLETED: 'quote_tokenize_completed',
  QUOTE_TOKENIZE_FAILED: 'quote_tokenize_failed',
  QUOTE_TOKENIZE_BLOCKED: 'quote_tokenize_blocked',
  QUOTE_TOKENIZE_BLOCK_UI_SHOWN: 'quote_tokenize_block_ui_shown',
  QUOTE_TOKENIZE_BLOCK_CTA_CLICKED: 'quote_tokenize_block_cta_clicked',
  QUOTE_AGREEMENT_CARD_OPENED: 'quote_agreement_card_opened',
  QUOTE_CONSENT_CHECKED: 'quote_consent_checked',
  QUOTE_CONSENT_UNCHECKED: 'quote_consent_unchecked',
  QUOTE_PAGE_LEFT: 'quote_page_left',
  QUOTE_CTA_VARIANT_RENDERED: 'quote_cta_variant_rendered',
  STEP1_CTA_VARIANT_RENDERED: 'step1_cta_variant_rendered',

  // Engagement events
  STEP_ABANDONED: 'step_abandoned',
  OUT_OF_SERVICE_SHOWN: 'out_of_service_shown',

  // A/B test exposure
  EXPERIMENT_EXPOSED: 'experiment_exposed',

  // Business outcome events (sent from Zapier/backend)
  DEAL_WON: 'deal_won',
  DEAL_LOST: 'deal_lost',
};

/**
 * Generate or retrieve a stable user ID for the session
 * Uses sessionStorage to maintain consistency across the user journey
 */
export const getUserId = () => {
  let userId = sessionStorage.getItem('statsig_user_id');

  if (!userId) {
    // Generate a new user ID using timestamp + random string
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('statsig_user_id', userId);
  }

  return userId;
};

let _cachedStableId = null;

export function deriveCookieDomain() {
  try {
    const origin = brandConfig.siteOrigin || 'cabanapools.com';
    const parts = origin.split('.');
    if (parts.length >= 2) return '.' + parts.slice(-2).join('.');
  } catch { /* fall through */ }
  return '.cabanapools.com';
}

const getOrCreateSharedStableId = () => {
  if (_cachedStableId) return _cachedStableId;

  const match = document.cookie.match(/(?:^|;\s*)_cabana_sid=([^;]*)/);
  if (match) {
    _cachedStableId = match[1];
    return _cachedStableId;
  }

  try {
    const stored = localStorage.getItem('_cabana_sid');
    if (stored) {
      _cachedStableId = stored;
      return _cachedStableId;
    }
  } catch { /* localStorage unavailable */ }

  const sid = `sid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  _cachedStableId = sid;

  const cookieDomain = deriveCookieDomain();
  document.cookie = `_cabana_sid=${sid}; domain=${cookieDomain}; path=/; max-age=31536000; SameSite=Lax`;

  const verifyMatch = document.cookie.match(/(?:^|;\s*)_cabana_sid=([^;]*)/);
  if (!verifyMatch) {
    try { localStorage.setItem('_cabana_sid', sid); } catch { /* best-effort */ }
  }

  return sid;
};

/**
 * Get the shared stable ID, creating it if needed.
 * The React app uses statsig-react v2 (statsig-js v5) which respects
 * overrideStableID in StatsigProvider options, so no localStorage
 * pre-seeding is needed (that was for the v3 CDN SDK on the homepage).
 */
export const getAndSeedSharedStableId = () => {
  return getOrCreateSharedStableId();
};

/**
 * Read the stable ID for inclusion in form payloads.
 * Single source of truth so Step.jsx and useAutoSubmitQuote stay aligned
 * with whatever cookie/storage logic getOrCreateSharedStableId uses.
 */
export const getStatsigStableId = () => getOrCreateSharedStableId();

const selfServeDecisionKey = forced => `${EXPERIMENTS.SELF_SERVE_FORM}:route:${getStatsigStableId()}:${forced || 'assigned'}`;

export const readSelfServeDecision = forced => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(selfServeDecisionKey(forced)));
    if (saved && typeof saved.showSelfServe === 'boolean' && typeof saved.assigned === 'boolean') {
      return { ...saved, expose: false };
    }
  } catch { /* Keep the mounted route when storage is unavailable. */ }
  return null;
};

// Resolve immediately before routing, after any asynchronous lane reservation.
// Provider readiness alone also includes SDK initialization failures. A real
// control has a group and rule just like treatment; fallback values do not.
export const captureSelfServeDecision = forced => {
  const saved = readSelfServeDecision(forced);
  if (saved) return saved;
  let config;
  try { config = Statsig.getExperimentWithExposureLoggingDisabled(EXPERIMENTS.SELF_SERVE_FORM); }
  catch { /* An unavailable SDK leaves the ordinary form usable. */ }
  const reason = config?.getEvaluationDetails?.()?.reason || 'Uninitialized';
  const rule = config?.getRuleID?.();
  const group = config?.getGroupName?.();
  const value = config?.getValue?.('show_self_serve');
  const assigned = ['Network', 'NetworkNotModified', 'Cache', 'Sticky', 'Bootstrap', 'Prefetch'].includes(reason)
    && typeof rule === 'string' && !!rule && rule !== 'default'
    && typeof group === 'string' && !!group && typeof value === 'boolean'
    // Console stableID overrides are assigned during setup too. The SDK
    // marks them inactive, with this server rule suffix; local overrides
    // still fail the evaluation-reason check above.
    && (config?.getIsExperimentActive?.() !== false || rule.endsWith(':stableID:id_override'));
  const qa = forced === 'control' || forced === 'treatment';
  const decision = { showSelfServe: qa ? forced === 'treatment' : assigned && value,
    assigned: assigned && !qa, reason, rule, group };
  try { sessionStorage.setItem(selfServeDecisionKey(forced), JSON.stringify(decision)); }
  catch { /* The caller also pins this decision for the mounted flow. */ }
  return { ...decision, expose: decision.assigned };
};

/**
 * Get the Statsig user object with custom attributes for targeting
 * This enriches the user with context for better experiment targeting
 */
export const getStatsigUser = () => {
  const userId = getUserId();
  const sharedStableId = getOrCreateSharedStableId();

  // Read landing page variant from URL param (set by homepage CTA link)
  const params = new URLSearchParams(window.location.search);
  const lpVariant = params.get('lp_variant');

  const custom = {
    brand: brandConfig.key || 'cabana',
    site_origin: brandConfig.siteOrigin || 'cabanapools.com',

    utm_source: localStorage.getItem('utm_source') || undefined,
    utm_medium: localStorage.getItem('utm_medium') || undefined,
    utm_campaign: localStorage.getItem('utm_campaign') || undefined,
    utm_content: localStorage.getItem('utm_content') || undefined,
    utm_term: localStorage.getItem('utm_term') || undefined,
    utm_device: localStorage.getItem('utm_device') || undefined,

    gclid: localStorage.getItem('gclid') || undefined,
    gbraid: localStorage.getItem('gbraid') || undefined,
    wbraid: localStorage.getItem('wbraid') || undefined,
    fbclid: localStorage.getItem('fbclid') || undefined,
    msclkid: localStorage.getItem('msclkid') || undefined,
    oppref: localStorage.getItem('oppref') || undefined,

    url: window.location.href,
    referrer: document.referrer || undefined,

    ga4_id: window.ga4_id || undefined,

    lp_variant: lpVariant || undefined,
  };

  // Remove undefined values to keep the object clean
  Object.keys(custom).forEach(key => {
    if (custom[key] === undefined) {
      delete custom[key];
    }
  });

  const user = {
    userID: userId,
    custom,
  };

  // Pass shared stable ID so Statsig can join landing page exposures with form events
  if (sharedStableId) {
    user.customIDs = { stableID: sharedStableId };
  }

  return user;
};

/**
 * Track a funnel step completion event
 * Uses the Statsig instance from statsig-react provider
 * @param {string} eventName - Name of the event to track
 * @param {object} metadata - Additional metadata about the event
 */
export const trackEvent = (eventName, metadata = {}) => {
  try {
    // Add common metadata
    const enrichedMetadata = {
      ...metadata,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    // Use Statsig from statsig-react (initialized by StatsigProvider)
    Statsig.logEvent(eventName, null, enrichedMetadata);
  } catch (error) {
    console.error('Error tracking Statsig event:', error);
  }
};

/**
 * Track time to complete from start to finish
 * Call startTimer when form begins, call endTimer when complete
 */
let formStartTime = null;

export const startFormTimer = () => {
  formStartTime = Date.now();
  sessionStorage.setItem('form_start_time', formStartTime.toString());
};

export const getFormDuration = () => {
  const startTime = formStartTime || parseInt(sessionStorage.getItem('form_start_time'), 10);

  if (!startTime) {
    return null;
  }

  const duration = Math.floor((Date.now() - startTime) / 1000); // Duration in seconds
  return duration;
};

/**
 * Track when a user abandons a specific step
 * @param {number} stepNumber - The step number where abandonment occurred
 */
export const trackStepAbandonment = (stepNumber) => {
  trackEvent(EVENTS.STEP_ABANDONED, {
    step_number: stepNumber,
    duration: getFormDuration(),
  });
};

/**
 * Force a specific experiment variant (for testing only)
 * Call this in browser console: window.forceExperimentVariant('treatment') or window.forceExperimentVariant('control')
 * Then refresh the page
 */
export const forceExperimentVariant = (variant) => {
  if (variant === 'treatment') {
    sessionStorage.setItem('force_experiment_variant', 'treatment');
    console.log('Forced experiment to TREATMENT variant. Refresh the page.');
  } else if (variant === 'control') {
    sessionStorage.setItem('force_experiment_variant', 'control');
    console.log('Forced experiment to CONTROL variant. Refresh the page.');
  } else {
    sessionStorage.removeItem('force_experiment_variant');
    console.log('Removed forced variant. Will use Statsig assignment. Refresh the page.');
  }
};

/**
 * Resolve step-1 button copy. Pure besides optional sessionStorage read.
 * Non-Cabana brands always stay on control. QA can force via
 * ?step1_cta=test|my|control or sessionStorage.force_step1_cta.
 */
export const resolveStep1CtaCopy = ({
  experimentCta,
  brand,
  search = '',
  sessionStore = null,
} = {}) => {
  if (brand && brand !== 'cabana') return STEP1_CTA_CONTROL;

  let forced = null;
  try {
    const params = new URLSearchParams(search || '');
    forced = params.get('step1_cta')
      || (sessionStore && sessionStore.getItem && sessionStore.getItem('force_step1_cta'));
  } catch { /* ignore unreadable search/storage */ }

  if (forced === 'test' || forced === 'my') return STEP1_CTA_TREATMENT;
  if (forced === 'control') return STEP1_CTA_CONTROL;
  return experimentCta || STEP1_CTA_CONTROL;
};

export const forceStep1Cta = (variant) => {
  if (variant === 'test' || variant === 'my' || variant === 'treatment') {
    sessionStorage.setItem('force_step1_cta', 'test');
  } else if (variant === 'control') {
    sessionStorage.setItem('force_step1_cta', 'control');
  } else {
    sessionStorage.removeItem('force_step1_cta');
  }
};

// Expose to window for console access
if (typeof window !== 'undefined') {
  window.forceExperimentVariant = forceExperimentVariant;
  window.forceStep1Cta = forceStep1Cta;
}
